/**
 * ROUND8 §4: what a dog sounds like at each moment, decided from the event and the dog's own state (`Enemy.dog`).
 * A sim event says only that a dog gave tongue, or that an enemy was hit; the dog's mode says which sound that is.
 * The growl is not an event at all: it is a rhythm in STATE - a dog running his trail, near him - so the engine's
 * `Pulses` asks `Growls` each frame. Pure: no WebAudio, so all of it is tested in node.
 */

import { isDog } from '../sim/enemies/specials/kinds';
import type { Enemy, SimEvent } from '../sim/types';
import type { SfxId } from './sfx';

/** A dog on his trail growls while it is this near him, metres - about what the view shows either side of him. */
export const GROWL_REACH = 20;
/** Seconds between one dog's growls; each dog is a little off this (`growlPeriod`), so two never growl in step. */
export const GROWL_EVERY = 2.4;

function enemyById(enemies: readonly Enemy[], id: number): Enemy | undefined {
  for (const e of enemies) if (e.id === id) return e;
  return undefined;
}

/**
 * The sound for this event once the dog behind it is known: at bay a double bark, a dog hit yelps; anything else
 * is `id` unchanged. A dog's bark in any other mode - on the trail, mourning its handler - is the single woof.
 */
export function dogSound(e: SimEvent, id: SfxId, enemies: readonly Enemy[]): SfxId {
  if (e.type === 'bark' && e.kind === 'dogBark') return enemyById(enemies, e.id)?.dog?.mode === 'bay' ? 'dogBay' : id;
  if (e.type === 'enemyHit') {
    const who = enemyById(enemies, e.id);
    return who !== undefined && isDog(who.kind) ? 'dogYelp' : id;
  }
  return id;
}

/** Is this dog closing on him: alive, running his trail, and near enough to be heard doing it? */
export function closing(e: Enemy, rangerX: number, rangerZ: number): boolean {
  if (!isDog(e.kind) || e.state === 'dead' || e.dog?.mode !== 'track') return false;
  return (e.x - rangerX) * (e.x - rangerX) + (e.z - rangerZ) * (e.z - rangerZ) <= GROWL_REACH * GROWL_REACH;
}

/** One dog's seconds between growls, 0.85 .. 1.15 of `GROWL_EVERY`, fixed by its id. */
export function growlPeriod(id: number): number {
  return GROWL_EVERY * (0.85 + (((Math.imul(id, 0x2545f491) >>> 0) % 1000) / 1000) * 0.3);
}

/**
 * Each dog's growl clock. A dog that starts closing growls at once - picking up his trail near him is the news -
 * then once a period while it keeps closing. The clock keeps running when it stops, so a dog that flickers
 * between casting and tracking does not growl on every flicker.
 */
export class Growls {
  /** Seconds since each dog last growled. */
  private readonly since = new Map<number, number>();

  reset(): void {
    this.since.clear();
  }

  /** Advance every dog's clock by `dt` seconds; `growl` is called for each one that growls now. */
  step(enemies: readonly Enemy[], rangerX: number, rangerZ: number, dt: number, growl: (dog: Enemy) => void): void {
    for (const e of enemies) {
      if (!isDog(e.kind)) continue;
      if (e.state === 'dead') {
        this.since.delete(e.id);
        continue;
      }
      let since = (this.since.get(e.id) ?? Number.POSITIVE_INFINITY) + dt;
      if (closing(e, rangerX, rangerZ) && since >= growlPeriod(e.id)) {
        growl(e);
        since = 0;
      }
      this.since.set(e.id, since);
    }
  }
}
